import type { RaidInsightItemDto } from "../../../api/raid";
import { ClientTable, Tabs } from "../../shared";
import {
  BulletList,
  ConfidenceBadge,
  DetailAccordionGroup,
  DetailAccordionSection,
  DetailKeyMessage,
  DetailMetadataStrip,
  DetailModalShell,
  DetailSubsection,
  DueDateWithCountdown,
  LinkedItems,
  PriorityBadge,
  TagList,
} from "../AiInsightsRaidDetailParts";
import type { RaidAssumptionsTopItemDto } from "./AiInsightsRaidAssumptionsTypes";
import { formatRaidInsightDaysLeft, formatRaidInsightDueDate } from "./raidInsightDisplay";

export type AiInsightsRaidAssumptionsDetailModalProps = {
  item: RaidAssumptionsTopItemDto;
  sourceItem?: RaidInsightItemDto;
  asAtDate?: string | null;
  resolvedItemsById?: ReadonlyMap<string, RaidInsightItemDto>;
  onClose: () => void;
};

interface AssumptionOverviewRow {
  assumptionId: string;
  workstream: string;
  priority: string;
  validationStatus: string;
}

interface ValidationAssessmentRow {
  owner: string;
  validator: string;
  dueDate: string;
  daysUntilDueDate: string;
}

export function AiInsightsRaidAssumptionsDetailModal({
  item,
  sourceItem,
  asAtDate,
  resolvedItemsById,
  onClose,
}: AiInsightsRaidAssumptionsDetailModalProps) {
  const overviewRow: AssumptionOverviewRow = {
    assumptionId: sourceItem?.itemId ?? item.item_id ?? "Source item unavailable",
    workstream: sourceItem?.workstream ?? "Not available",
    priority: item.criticality ?? "Not available",
    validationStatus: sourceItem?.status ?? "Not available",
  };

  const validityFactors = Array.isArray(item.basis_analysis?.evidence_supported_validity_factors)
    ? item.basis_analysis.evidence_supported_validity_factors
    : [];
  const unknowns = Array.isArray(item.basis_analysis?.unknowns) ? item.basis_analysis.unknowns : [];
  const basisAnalysisItems = [
    item.basis_analysis?.recorded_basis,
    ...validityFactors.map((factor) => factor.factor),
    ...unknowns.map((unknown) => "Unknown: " + unknown),
  ].filter((entry): entry is string => entry != null && entry.trim().length > 0);

  const affectedWorkstreams = Array.isArray(item.impact_if_incorrect?.affected_workstreams)
    ? item.impact_if_incorrect.affected_workstreams
    : [];
  const affectedMilestones = Array.isArray(
    item.impact_if_incorrect?.affected_milestones_or_activities
  )
    ? item.impact_if_incorrect.affected_milestones_or_activities
    : [];
  const affectedObjectives = Array.isArray(item.impact_if_incorrect?.affected_objectives_or_value)
    ? item.impact_if_incorrect.affected_objectives_or_value
    : [];

  const linkedItems = Array.isArray(item.linked_items) ? item.linked_items : [];
  const whatWeNeedToKnow = Array.isArray(item.what_we_need_to_know)
    ? item.what_we_need_to_know
    : [];
  const recommendedNextSteps =
    item.recommended_focus != null && item.recommended_focus.trim().length > 0
      ? [item.recommended_focus]
      : [];
  const whyThisMattersItems = [
    item.why_it_is_important_now,
    item.why_priority_for_this_user,
  ].filter((message): message is string => message != null && message.trim().length > 0);

  const validationAssessmentRows: ValidationAssessmentRow[] = [
    {
      owner: sourceItem?.owner ?? "Not available",
      validator: sourceItem?.validator ?? "Not available",
      dueDate: formatRaidInsightDueDate(sourceItem?.dueDate),
      daysUntilDueDate: formatRaidInsightDaysLeft(sourceItem?.dueDate, asAtDate),
    },
  ];
  const impactSummary =
    item.impact_if_incorrect?.potential_impact ??
    item.impact_if_incorrect?.risk_or_issue_if_incorrect ??
    "Not available";

  return (
    <DetailModalShell
      title={sourceItem?.title ?? "Source item unavailable"}
      onClose={onClose}
      className="global-insights-overlay__risk-detail-modal--assumption"
      lastUpdatedAt={sourceItem?.lastUpdatedAt}
    >
      <DetailMetadataStrip
        items={[
          {
            label: "Assumption ID",
            value: overviewRow.assumptionId,
            provenance: sourceItem?.itemId != null ? "Source record" : "AI assessed",
          },
          { label: "Workstream", value: overviewRow.workstream, provenance: "Source record" },
          {
            label: "Priority",
            value: (
              <span className="global-insights-overlay__ai-source-value">
                <PriorityBadge value={overviewRow.priority} />
              </span>
            ),
            provenance: "AI assessed",
          },
          {
            label: "Validation status",
            value: overviewRow.validationStatus,
            provenance: "Source record",
          },
          {
            label: "Confidence",
            value: (
              <span className="global-insights-overlay__ai-source-value">
                <ConfidenceBadge value={item.confidence} />
              </span>
            ),
            provenance: "AI assessed",
          },
        ]}
      />

      <DetailKeyMessage label="Impact if assumption is incorrect">{impactSummary}</DetailKeyMessage>

      <Tabs
        variant="underline"
        className="global-insights-overlay__risk-detail-tabs"
        items={[
          {
            key: "assessment",
            label: "Assessment",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection
                  heading="Basis analysis"
                  aiGenerated
                  itemKey="basis-analysis"
                >
                  <div className="global-insights-overlay__risk-detail-cause-analysis">
                    <BulletList items={basisAnalysisItems} />
                  </div>
                </DetailAccordionSection>
                <DetailAccordionSection
                  heading="Full impact analysis"
                  aiGenerated
                  itemKey="impact-analysis"
                >
                  <div className="global-insights-overlay__risk-detail-grey-box global-insights-overlay__risk-detail-impact-panel">
                    <DetailSubsection label="Signs assumption may be invalid">
                      <p className="global-insights-overlay__risk-detail-text">
                        {item.impact_if_incorrect?.observed_signs_of_invalidity ?? "Not available"}
                      </p>
                    </DetailSubsection>
                    {item.impact_if_incorrect?.potential_impact !== impactSummary && (
                      <DetailSubsection label="Potential impact">
                        <p className="global-insights-overlay__risk-detail-text">
                          {item.impact_if_incorrect?.potential_impact ?? "Not available"}
                        </p>
                      </DetailSubsection>
                    )}
                    {item.impact_if_incorrect?.risk_or_issue_if_incorrect !== impactSummary && (
                      <DetailSubsection label="Resulting risk or issue">
                        <p className="global-insights-overlay__risk-detail-text">
                          {item.impact_if_incorrect?.risk_or_issue_if_incorrect ?? "Not available"}
                        </p>
                      </DetailSubsection>
                    )}
                    <DetailSubsection label="Affected workstreams">
                      <TagList items={affectedWorkstreams} />
                    </DetailSubsection>
                    <DetailSubsection label="Affected milestones">
                      <TagList items={affectedMilestones} />
                    </DetailSubsection>
                    <DetailSubsection label="Affected objectives">
                      <TagList items={affectedObjectives} />
                    </DetailSubsection>
                  </div>
                </DetailAccordionSection>
                <DetailAccordionSection
                  heading="Why this matters now"
                  aiGenerated
                  itemKey="why-this-matters"
                >
                  <DetailSubsection label="Time sensitivity">
                    <p className="global-insights-overlay__risk-detail-text">
                      {item.time_sensitivity ?? "Not available"}
                    </p>
                  </DetailSubsection>
                  <BulletList items={whyThisMattersItems} />
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
          {
            key: "validation",
            label: "Validation & evidence",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection
                  heading="Validation assessment"
                  itemKey="validation-assessment"
                >
                  <ClientTable
                    className="global-insights-overlay__risk-detail-table global-insights-overlay__risk-detail-table--borderless"
                    data={validationAssessmentRows}
                    rowKey={() => "validation-assessment"}
                    columns={[
                      { id: "owner", header: "Owner", accessor: "owner" },
                      { id: "validator", header: "Validator", accessor: "validator" },
                      {
                        id: "dueDate",
                        header: "Due Date",
                        renderCell: (row) => (
                          <DueDateWithCountdown
                            dueDate={row.dueDate}
                            daysUntilDueDate={row.daysUntilDueDate}
                          />
                        ),
                      },
                    ]}
                  />
                </DetailAccordionSection>
                <DetailAccordionSection
                  heading="Linked items"
                  aiGenerated
                  itemKey="linked-items"
                  badge={linkedItems.length}
                >
                  <LinkedItems items={linkedItems} resolvedItemsById={resolvedItemsById} />
                </DetailAccordionSection>
                <DetailAccordionSection
                  heading="What we need to know"
                  aiGenerated
                  itemKey="what-we-need-to-know"
                >
                  <div className="global-insights-overlay__risk-detail-cause-analysis">
                    <BulletList items={whatWeNeedToKnow} />
                  </div>
                </DetailAccordionSection>
                <DetailAccordionSection
                  heading="Recommended next steps"
                  aiGenerated
                  itemKey="recommended-next-steps"
                >
                  <div className="global-insights-overlay__risk-detail-recommended-steps">
                    <BulletList items={recommendedNextSteps} />
                  </div>
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
        ]}
      />
    </DetailModalShell>
  );
}

export default AiInsightsRaidAssumptionsDetailModal;
